import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { deleteAddress } from '../../../../../store/slices/addresses/editAddressSlice';
import { getAddresses, getStates } from '../../../../../store/slices/addresses/addressesSlice';
import Searcher from '../../../../molecules/Searcher';
import Table from '../../../../atoms/Table/Table';
import EmptyBlock from '../../../../molecules/EmptyBlock';
import { columns, MAX_AMOUNT_OF_ITEMS_ON_PAGE } from './AddressesTable.constants';
import MButton from '../../../../MUI/Button/MButton';
import Dropdown from '../../../../atoms/Dropdown';
import { rolesDB, useAllowed } from '../../../../../utils/roleHelpers';
import useSafeLocationState from '../../../../../hooks/useSafeLocationState';
import StateLink from '../../../../atoms/StateLink/StateLink';
import BottomBar from '../../../../atoms/BottomBar/BottomBar';
import { usePrimaryState } from '../../../../../hooks/usePrimaryState';

const AddressesTable = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const primaryState = usePrimaryState();

  const [searchValue, setSearchValue] = useState('');
  const [isLoader, setIsLoader] = useState(true);
  const [isLoaderTable, setIsLoaderTable] = useState(false);
  const {
    navPage,
    subNav,
    state: defaultState,
  } = useSafeLocationState('Data Handling', 'Addresses');
  const { addresses, states } = useSelector((state) => state.addresses);
  const [state, setState] = useState(primaryState || defaultState || null);

  const canCreate = useAllowed([rolesDB.Admin, rolesDB.Accounting, rolesDB.Attorney]);
  const canDelete = useAllowed([rolesDB.Admin]);

  const getAddressesData = useCallback(
    ({ currentPage = null, address = null, stateCode = state?.value } = {}) => {
      if (stateCode !== state?.value) setIsLoader(true);
      const params = {
        pageSize: MAX_AMOUNT_OF_ITEMS_ON_PAGE,
        stateCode,
      };
      if (currentPage !== null) params.currentPage = currentPage;
      if (address !== null) params.address = address;
      return dispatch(getAddresses(params)).then(() => setIsLoader(false));
    },
    [dispatch, setIsLoader, state],
  );

  const handleFetchDataPage = useCallback(
    (currentPage) => {
      setIsLoaderTable(true);
      getAddressesData({
        currentPage,
        address: searchValue,
      }).then(() => {
        setIsLoaderTable(false);
      });
    },
    [searchValue, getAddressesData],
  );

  const handleChangeEmpty = useCallback(
    (searchElement) => {
      if (!searchElement) {
        setIsLoaderTable(true);
        getAddressesData({ pageSize: MAX_AMOUNT_OF_ITEMS_ON_PAGE }).then(() => {
          setIsLoaderTable(false);
          setSearchValue('');
        });
      }
    },
    [getAddressesData],
  );

  const handleSearch = useCallback(
    (searchElement) => {
      setSearchValue(searchElement);
      setIsLoaderTable(true);
      getAddressesData({ address: searchElement }).then(() => setIsLoaderTable(false));
    },
    [getAddressesData],
  );

  const handleCreateAddress = useCallback(() => {
    navigate('/db/datahandling/newaddress/address', {
      state: {
        navPage,
        subNav,
        state,
      },
    });
  }, [state]);

  const handleDeleteAddress = useCallback(
    (id, original) => {
      return dispatch(deleteAddress(original.addressId));
    },
    [getAddressesData],
  );

  const handleStateChange = useCallback(
    (value) => {
      setState(value);
      setIsLoaderTable(true);
      getAddressesData({
        courtName: searchValue,
        stateCode: value?.value,
      }).then(() => setIsLoaderTable(false));
    },
    [getAddressesData],
  );

  useEffect(() => {
    dispatch(getStates());
  }, []);

  useEffect(() => {
    if (states.length) {
      handleStateChange(primaryState || defaultState || states[0]);
    }
  }, [states]);

  const rowsAdapter = addresses?.items?.map((item) => ({
    ...item,
    address: (
      <StateLink
        to="/db/datahandling/editaddress/address"
        state={{ navPage, subNav, id: item.addressId, name: item.address, state }}
      >
        <Typography
          variant="link"
          color="text.primary"
          sx={{
            cursor: 'pointer',
            textDecorationLine: 'underline',
            textTransform: 'none',
          }}
        >
          {item.address}
        </Typography>
      </StateLink>
    ),
    control: {
      hideDelete: !canDelete,
      autoRefresh: true,
    },
  }));

  if (!isLoader && !rowsAdapter?.length && !searchValue) {
    return (
      <Stack height="calc(100vh - 80px - 72px)" direction="column">
        <Stack display="flex" flexDirection="row">
          <Typography variant="h3" margin="0 32px">
            Address Search
          </Typography>
          <Dropdown
            label="Select state"
            options={states}
            onChange={handleStateChange}
            value={state}
          />
        </Stack>
        <Box margin="auto">
          <EmptyBlock
            title="You don’t have any addresses yet"
            desc={
              canCreate
                ? 'Every address you create will appear here. To get started click on “Create new address” button below'
                : ''
            }
            buttonText={canCreate ? 'Create new address' : ''}
            onClick={canCreate ? handleCreateAddress : undefined}
          />
        </Box>
      </Stack>
    );
  }

  return (rowsAdapter?.length && !isLoader) || searchValue ? (
    <>
      <Box width="1066px" marginX="auto" pb={18}>
        <Stack spacing={3}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h5">Addresses</Typography>
            <Box paddingTop={1}>
              <Dropdown
                label="Select state"
                options={states}
                onChange={handleStateChange}
                value={state}
              />
            </Box>
          </Stack>
          <Searcher
            inputPlaceholder="Search by address"
            onSearch={handleSearch}
            onChange={handleChangeEmpty}
          />
          {!isLoader && !rowsAdapter?.length && searchValue ? (
            <Box paddingTop="100px" display="flex" justifyContent="center">
              <EmptyBlock
                title="Addresses are not found"
                desc="Сhange the search value"
                margin="auto"
              />
            </Box>
          ) : (
            <Table
              columns={columns}
              rows={rowsAdapter}
              total={addresses?.totalRowsCount}
              isPagination={addresses?.totalPages > 1}
              onNextPage={handleFetchDataPage}
              onPreviousPage={handleFetchDataPage}
              onGotoPage={handleFetchDataPage}
              pageSize={MAX_AMOUNT_OF_ITEMS_ON_PAGE}
              onDeleteRow={handleDeleteAddress}
              onRefreshPage={handleFetchDataPage}
              loading={isLoaderTable}
            />
          )}
        </Stack>
      </Box>
      {canCreate && (
        <BottomBar>
          <Stack spacing={2} direction="row" justifyContent="flex-end">
            <MButton width={144} size="large" onClick={handleCreateAddress}>
              Create New
            </MButton>
          </Stack>
        </BottomBar>
      )}
    </>
  ) : (
    <Box position="absolute" top="50vh" left="50%" zIndex={10} transform="translate(-50%, -50%)">
      <CircularProgress />
    </Box>
  );
};

export default AddressesTable;
